import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

import { CssVarsProvider } from "@mui/joy/styles";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Provider } from "react-redux";
import { store } from "./state/redux/store.ts";
import theme from "./theme.js";
import { HGNow } from "./common/types.ts";
import { calculateNow } from "./common/now.ts";
import { createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import Records from "./components/ui/hg/Records.tsx";
import Home from "./components/ui/hg/Home.tsx";
import { checkAuthorization } from "./common/auth.ts";
import PasswordLogin from "./components/ui/hg/PasswordLogin.tsx";
import AdminPage from "./components/ui/hg/AdminPage.tsx";
import OAuthCallback from "./components/ui/hg/OAuthCallback.tsx";
import RecordPage from "./components/ui/hg/RecordPage.tsx";
import RouteError from "./components/ui/hg/RouteError.tsx";
import { isApiFailure } from "./api/errors.ts";
import { notifyError } from "./common/notify.ts";
import { initGlobalStacks } from "./state/globalStacks.ts";
import { initModule } from "./engine/engine.ts";

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Provider store={store}>
        <GoogleOAuthProvider clientId="309121013967-scrdtlvqotheutn1628blq31a2t5fbq4.apps.googleusercontent.com">
          <CssVarsProvider theme={theme}>
            <App />
          </CssVarsProvider>
        </GoogleOAuthProvider>
      </Provider>
    ),
    errorElement: <RouteError />,
    children: [
      // The timeline signed in, the landing page signed out. See `Home`.
      {
        index: true,
        element: <Home />,
      },
      {
        path: "/login",
        element: <PasswordLogin />,
      },
      {
        path: "/admin",
        element: <AdminPage />,
      },
      // Where the OAuth popup lands on its way back from a provider. Normally
      // renders nothing: it posts the outcome to the window that opened it and
      // closes. "oauth" is a reserved nickname for the same reason "record" is.
      {
        path: "/oauth/callback",
        element: <OAuthCallback />,
      },
      // React Router ranks static segments above dynamic ones, so "/admin"
      // and "/login" above always win over ":userID" here regardless of
      // array order -- kept next to each other for readability, not because
      // order matters.
      {
        path: "/:userID",
        element: <Records />,
      },
      // Three segments with a literal "-" in the middle, so it never collides
      // with the two-segment collection route. "-" is a reserved slug.
      {
        path: "/:userID/-/:recordSlug",
        element: <RecordPage />,
      },
      {
        path: "/:userID/:collectionID",
        element: <Records />,
      },
      // Legacy permalink. Kept indefinitely: it is the only URL a record
      // without a slug has, and it is why "record" is a reserved nickname.
      {
        path: "/record/:recordID",
        element: <RecordPage />,
      },
    ],
  },
]);

// The safety net under every call site: a request that failed and that
// nothing caught still tells the user. Only API failures -- anything else
// unhandled is a bug or a library's noise, and not the user's to act on.
window.addEventListener("unhandledrejection", (event) => {
  if (isApiFailure(event.reason)) notifyError(event.reason);
});

// fix viewport on mobile devices (from chatgpt)
// const vh = window.innerHeight * 0.01;
// document.documentElement.style.setProperty("--vh", `${vh}px`);

let globalNow: HGNow = {};

export function setNow(now: HGNow) {
  globalNow = now;
}

export function getNow() {
  return globalNow;
}

// Started before the engine rather than after it. The two are independent —
// one is a wasm download, the other a round trip to /user/ — and awaiting them
// in series used to put two full latencies in front of the first paint, and
// therefore in front of the first record request as well.
const authorized = checkAuthorization();

initModule().then(async () => {
  setNow(calculateNow());
  setInterval(() => {
    setNow(calculateNow());
  }, 1000);

  // Needs the engine: constructing the stacks names the current interval.
  initGlobalStacks(router);

  await authorized;

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <RouterProvider router={router} />,
  );
});
